import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../api/api";
import Footer from "../../Footer/FooterBottom";
import ShowQuestionModal from "../../Home/Tracks/ExamView/ShowQuestionModal";
import SingleQuestionView from "../Question/View/QuestionView";
import CategoryTree from "./components/CategoryTree";
import QuestionsList from "./components/QuestionsList";
import { normalizeArabicText, stripHtml } from "./utils/arabicNormalizer";
import "./QuestionView.css";

const QuestionView = () => {
    const { t } = useTranslation("common");
    const history = useHistory();
    const user = useSelector(state => state.user);

    // Permission check: admin is strictly user type id 1
    const isAdmin = user && user.user && user.user.UserType && user.user.UserType.Id === 1;

    // Component State
    const [categoriesTree, setCategoriesTree] = useState([]);
    const [categoriesList, setCategoriesList] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [loadingTree, setLoadingTree] = useState(false);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    // Collapsed states of category tree nodes (keyed by CategoryId, default to collapsed / null active)
    const [openCategoryId, setOpenCategoryId] = useState(null);

    // Search Filters
    const [topicSearchQuery, setTopicSearchQuery] = useState("");
    const [questionSearchQuery, setQuestionSearchQuery] = useState("");

    // Form States (Create Topic)
    const [newTopicName, setNewTopicName] = useState("");
    const [newTopicCategoryId, setNewTopicCategoryId] = useState("");
    const [showCreateForm, setShowCreateForm] = useState(false);

    // Form States (Rename Topic)
    const [editingTopicId, setEditingTopicId] = useState(null);
    const [editingTopicName, setEditingTopicName] = useState("");

    // Modal & Preview States
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [showQuestionModal, setShowQuestionModal] = useState(false);
    const [fetchingQuestionId, setFetchingQuestionId] = useState(null);

    useEffect(() => {
        if (isAdmin) {
            fetchCategoriesTree();
            fetchCategoriesList();
        } else if (user && user.isLogin === false) {
            history.push("/Login");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, user]);

    // Fetch questions when topic changes
    useEffect(() => {
        if (selectedTopic) {
            fetchQuestions(selectedTopic.TopicId);
        } else {
            setQuestions([]);
        }
        setQuestionSearchQuery(""); // Clear search filter
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTopic]);

    // API: Fetch categories tree (grouped with topics and question counts)
    const fetchCategoriesTree = async (autoSelectFirst = true) => {
        setLoadingTree(true);
        try {
            const response = await api.get("/QuestionsLookups/TopicsCategories");
            const data = response.data.data || [];
            setCategoriesTree(data);

            // Auto select first topic of the first category if none selected
            if (autoSelectFirst && data.length > 0 && !selectedTopic) {
                const firstCat = data.find(c => c.Topics && c.Topics.length > 0);
                if (firstCat && firstCat.Topics.length > 0) {
                    setSelectedTopic(firstCat.Topics[0]);
                }
            }
        } catch (error) {
            console.error("Error fetching categories tree:", error);
            toast.error(t("ControlPanel.Indices.error.Title") || "حدث خطأ أثناء تحميل شجرة المواضيع");
        } finally {
            setLoadingTree(false);
        }
    };


    // API: Fetch simple categories list for dropdown selection
    const fetchCategoriesList = async () => {
        try {
            const response = await api.get("/QuestionsLookups/CategoriesList");
            setCategoriesList(response.data.data || []);
        } catch (error) {
            console.error("Error fetching categories list:", error);
        }
    };

    // API: Fetch questions for a topic
    const fetchQuestions = async (topicId) => {
        setLoadingQuestions(true);
        try {
            const response = await api.get(`/QuestionsLookups/Topics/${topicId}/Questions`);
            setQuestions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching questions:", error);
            toast.error(t("ControlPanel.Indices.error.Title") || "حدث خطأ أثناء تحميل الأسئلة");
        } finally {
            setLoadingQuestions(false);
        }
    };

    const handleViewQuestion = async (question) => {
        if (!question.QuestionId) return;
        setFetchingQuestionId(question.QuestionId);
        try {
            const response = await api.post("/QuestionsLookups", [
                {
                    IsFromDB: true,
                    QuestionId: question.QuestionId,
                    LessonId: question.LessonId
                }
            ]);
            const details = response.data.data?.[0];
            if (details) {
                setSelectedQuestion(details);
                setShowQuestionModal(true);
            } else {
                toast.error(t("QuestionView.errorLoadingDetails") || "تعذر تحميل تفاصيل السؤال");
            }
        } catch (error) {
            console.error("Error fetching question details:", error);
            toast.error(t("QuestionView.errorLoadingDetails") || "حدث خطأ أثناء تحميل تفاصيل السؤال");
        } finally {
            setFetchingQuestionId(null);
        }
    };

    // API: Create new topic at specific category
    const handleCreateTopic = async (e) => {
        e.preventDefault();
        if (!newTopicName.trim()) {
            toast.warn(t("QuestionView.titleRequired") || "يرجى إدخال اسم الموضوع");
            return;
        }

        const catIdValue = newTopicCategoryId ? parseInt(newTopicCategoryId) : 0;

        if (catIdValue < 1 || catIdValue > 5) {
            toast.warn(t("QuestionView.categoryRequired"));
            return;
        }

        try {
            const response = await api.post("/QuestionsLookups/Topics", {
                TopicName: newTopicName.trim(),
                CategoryId: catIdValue
            });
            const createdTopic = response.data.data;
            toast.success(t("ControlPanel.Indices.Success.Title") || "تم الحفظ بنجاح");

            // Reload the tree to update counts and hierarchy
            await fetchCategoriesTree(false);

            // Set newly created topic as selected
            setSelectedTopic({
                TopicId: createdTopic.TopicId,
                TopicName: createdTopic.TopicName,
                QuestionCount: 0
            });

            // Clear and close form
            setNewTopicName("");
            setNewTopicCategoryId("");
            setShowCreateForm(false);
        } catch (error) {
            console.error("Error creating topic:", error);
            toast.error(t("ControlPanel.Indices.error.Title") || "حدث خطأ أثناء إضافة الموضوع");
        }
    };

    // API: Rename topic
    const handleRenameTopic = async (e, id) => {
        e.preventDefault();
        if (!editingTopicName.trim()) {
            toast.warn(t("QuestionView.titleRequired") || "يرجى إدخال اسم الموضوع");
            return;
        }

        try {
            const response = await api.put(`/QuestionsLookups/Topics/${id}`, {
                TopicName: editingTopicName.trim()
            });
            const updatedTopic = response.data.data;
            toast.success(t("ControlPanel.Indices.Success.Title") || "تم الحفظ بنجاح");

            // Update tree locally without fetching
            setCategoriesTree(prev =>
                prev.map(cat => ({
                    ...cat,
                    Topics: cat.Topics.map(t =>
                        t.TopicId === id ? { ...t, TopicName: updatedTopic.TopicName } : t
                    )
                }))
            );

            if (selectedTopic && selectedTopic.TopicId === id) {
                setSelectedTopic(prev => ({ ...prev, TopicName: updatedTopic.TopicName }));
            }

            setEditingTopicId(null);
            setEditingTopicName("");
        } catch (error) {
            console.error("Error renaming topic:", error);
            toast.error(t("ControlPanel.Indices.error.Title") || "حدث خطأ أثناء تعديل الاسم");
        }
    };

    // Toggle expand/collapse for category nodes
    const toggleCategoryCollapse = (categoryId) => {
        setOpenCategoryId(prevId => prevId === categoryId ? null : categoryId);
    };

    // Filter categories tree based on search query
    const filteredCategoriesTree = useMemo(() => {
        if (!topicSearchQuery.trim()) return categoriesTree;

        const normalizedQuery = normalizeArabicText(topicSearchQuery.toLowerCase().trim());

        return categoriesTree.map(category => {
            // Filter topics under this category
            const matchingTopics = category.Topics.filter(topic => {
                const normalizedTopicName = normalizeArabicText(topic.TopicName.toLowerCase());
                return normalizedTopicName.includes(normalizedQuery);
            });

            const normalizedCategoryName = normalizeArabicText(category.CategoryName.toLowerCase());
            const isCategoryMatch = normalizedCategoryName.includes(normalizedQuery);

            if (isCategoryMatch || matchingTopics.length > 0) {
                return {
                    ...category,
                    Topics: matchingTopics // only show matching topics in search results
                };
            }
            return null;
        }).filter(Boolean);
    }, [categoriesTree, topicSearchQuery]);

    // Filter questions based on search query without html and without tashkeel
    const filteredQuestions = useMemo(() => {
        if (!questionSearchQuery.trim()) return questions;

        const normalizedQuery = normalizeArabicText(questionSearchQuery.toLowerCase().trim());

        return questions.filter(q => {
            const plainText = stripHtml(q.QuestionText);
            const normalizedText = normalizeArabicText(plainText.toLowerCase());
            return normalizedText.includes(normalizedQuery);
        });
    }, [questions, questionSearchQuery]);

    const startEditing = (topic) => {
        setEditingTopicId(topic.TopicId);
        setEditingTopicName(topic.TopicName);
    };

    const handleAccessDeniedBack = () => {
        history.push("/Profile");
    };

    if (!isAdmin) {
        return (
            <div className="access-denied-container">
                <Helmet>
                    <title>{t("QuestionView.accessDenied") || "Access Denied"} | {t("alarabeyya")}</title>
                </Helmet>
                <div className="access-denied-card text-center">
                    <div className="access-denied-icon">
                        <i className="fal fa-shield-ban"></i>
                    </div>
                    <h2 className="access-denied-title">{t("QuestionView.accessDenied")}</h2>
                    <p className="access-denied-text">{t("QuestionView.accessDeniedDesc")}</p>
                    <button className="btn btn-primary px-5 py-3 access-denied-btn" onClick={handleAccessDeniedBack}>
                        <i className="fal fa-arrow-right ml-2"></i>
                        <span>{t("QuestionView.cancel")}</span>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{t("QuestionView.title")} | {t("alarabeyya")}</title>
            </Helmet>

            <div className="question-view-admin-wrapper">
                {/* Header Banner */}
                <div className="admin-banner-header">
                    <div className="container-fluid px-lg-5 header-container-flex">
                        <div className="header-text-block">
                            <h2 className="banner-title">{t("QuestionView.title")}</h2>
                            <p className="banner-subtitle">{t("QuestionView.subtitle")}</p>
                        </div>
                        <div className="header-badge-block">
                            <div className="quick-stat-badge">
                                <span className="stat-value">
                                    {categoriesTree.reduce((acc, cat) => acc + (cat.Topics ? cat.Topics.length : 0), 0)}
                                </span>
                                <span className="stat-label">{t("QuestionView.topicCountLabel")}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="container-fluid px-lg-5 py-5 main-dashboard-body">
                    <div className="row">
                        {/* Left Sidebar: Collapsible Categories Tree */}
                        <CategoryTree
                            topicSearchQuery={topicSearchQuery}
                            setTopicSearchQuery={setTopicSearchQuery}
                            showCreateForm={showCreateForm}
                            setShowCreateForm={setShowCreateForm}
                            handleCreateTopic={handleCreateTopic}
                            newTopicName={newTopicName}
                            setNewTopicName={setNewTopicName}
                            newTopicCategoryId={newTopicCategoryId}
                            setNewTopicCategoryId={setNewTopicCategoryId}
                            categoriesList={categoriesList}
                            loadingTree={loadingTree}
                            filteredCategoriesTree={filteredCategoriesTree}
                            openCategoryId={openCategoryId}
                            toggleCategoryCollapse={toggleCategoryCollapse}
                            selectedTopic={selectedTopic}
                            setSelectedTopic={setSelectedTopic}
                            editingTopicId={editingTopicId}
                            setEditingTopicId={setEditingTopicId}
                            editingTopicName={editingTopicName}
                            setEditingTopicName={setEditingTopicName}
                            handleRenameTopic={handleRenameTopic}
                            startEditing={startEditing}
                            t={t}
                        />

                        {/* Right Content: Questions List & Lesson Links */}
                        <QuestionsList
                            selectedTopic={selectedTopic}
                            filteredQuestions={filteredQuestions}
                            questions={questions}
                            questionSearchQuery={questionSearchQuery}
                            setQuestionSearchQuery={setQuestionSearchQuery}
                            loadingQuestions={loadingQuestions}
                            fetchingQuestionId={fetchingQuestionId}
                            handleViewQuestion={handleViewQuestion}
                            t={t}
                        />
                    </div>
                </div>
            </div>

            <div className="position-sticky mt-auto">
                <Footer />
            </div>

            {showQuestionModal && selectedQuestion && (
                <ShowQuestionModal
                    showResult={true}
                    handleClose={() => {
                        setShowQuestionModal(false);
                        setSelectedQuestion(null);
                    }}
                    renderQuestionView={() => (
                        <SingleQuestionView
                            question={selectedQuestion}
                            key={selectedQuestion.Id}
                            index={1}
                            showResult={false}
                            onEdit={true}
                            solveButton={false}
                        />
                    )}
                />
            )}
        </>
    );
};

export default QuestionView;
